import { borderRadius as DEFAULT_BORDER_RADIUS } from '@ui/theme/values'
import Button from '@ui/buttons/Button'
import MagicGlow from '@ui/tiles/MagicGlow'
import { colors } from '@ui/colors'
import { BlurView } from 'expo-blur'
import { Image } from 'expo-image'
import React, { useState } from 'react'
import { Platform, StyleSheet, View, ViewProps } from 'react-native'
import Animated, {
  AnimatedProps,
  interpolate,
  interpolateColor,
  useAnimatedStyle,
  useSharedValue,
} from 'react-native-reanimated'
import { twMerge } from 'tailwind-merge'
import cn from '@ui/utils/cn'
import {
  DEFAULT_BLUR_MODE,
  DEFAULT_BLUR_OPACITY,
  DEFAULT_ONPRESS_BACKGROUND,
  iconBlurMode,
} from './styles'
import { BlurMode, TileGlassProps } from './types'

export default function TileGlass({
  children,
  className,
  style,
  isDev = false,
  blur = Platform.select({ default: 100, web: 50 }),
  blurStyle,
  blurWrapperStyle,
  blurClassName,
  blurContentFit = 'contain',
  blurImageProps,
  image,
  imageBlur,
  imageStyle,
  imageWrapperStyle,
  imageProps,
  gradient,
  backgroundColor = colors.gray[75],
  onPressBackground,
  underBlurComponent,
  magicGlow,
  borderRadius = DEFAULT_BORDER_RADIUS,
  ...props
}: TileGlassProps & AnimatedProps<ViewProps>) {
  const animation0 = useSharedValue(0)
  const animation = props.animation || animation0
  const [blurMode, setBlurMode] = useState<BlurMode>(
    props.blurMode || DEFAULT_BLUR_MODE
  )

  const blurOpacity =
    (blurStyle && StyleSheet.flatten(blurStyle).opacity) ?? DEFAULT_BLUR_OPACITY

  const onPressBG = onPressBackground || DEFAULT_ONPRESS_BACKGROUND

  const stylePressedContainer = useAnimatedStyle(() => {
    return {
      backgroundColor: interpolateColor(
        animation.value,
        [0, 1],
        [backgroundColor, onPressBG]
      ),
      borderColor: interpolateColor(
        animation.value,
        [0, 1],
        [colors.text10, colors.text10]
      ),
    }
  })

  const stylePressedBlur = useAnimatedStyle(() => {
    return {
      opacity: interpolate(animation.value, [0, 1], [blurOpacity as number, 1]),
    }
  })

  const stylePressedImageWrapper = useAnimatedStyle(() => {
    return {
      transform: [
        {
          scale: interpolate(animation.value, [0, 1], [1, 1.05]),
        },
      ],
    }
  })

  return (
    <>
      {/* TODO: add global dev setting for toggling this */}
      {isDev && (
        <Button
          className="absolute -right-4 -top-2"
          size="xs"
          variant="outlined"
          iconStart={iconBlurMode[blurMode]}
          onPress={() =>
            setBlurMode((prev) =>
              prev === BlurMode.backdropFilter
                ? BlurMode.blurRadius
                : BlurMode.backdropFilter
            )
          }
          style={{ display: 'none' }} // un/comment to hide/display
        >
          {blurMode}
        </Button>
      )}
      <Animated.View
        {...props}
        style={[
          {
            borderWidth: 0.8, // StyleSheet.hairlineWidth,
            backgroundColor,
            borderColor: colors.text10,
            borderRadius,
          },
          style,
          stylePressedContainer,
        ]}
        className={twMerge('relative overflow-hidden border', className)}
      >
        {underBlurComponent}
        {(imageBlur || image || !!underBlurComponent) && (
          <View
            className={cn(
              'absolute -bottom-32 -right-[70%] -top-40 left-0 flex-row justify-end',
              'overflow-hidden',
              blurClassName
            )}
            style={[{ borderRadius }, blurWrapperStyle]}
          >
            <Animated.View className="h-full w-full" style={[stylePressedBlur]}>
              <Image
                source={imageBlur || image}
                cachePolicy="memory-disk"
                contentFit={blurContentFit}
                transition={150}
                blurRadius={blurMode === BlurMode.blurRadius ? blur : 0}
                style={[
                  {
                    flex: 1,
                    // opacity: 0.5,
                  },
                  blurStyle,
                  {
                    opacity: 1, // while blurStyle includes opacity, we use it to create the onPress animation, applied on the parent container. so here the opacity from props needs to be overridden.
                  },
                ]}
                {...blurImageProps}
              />
            </Animated.View>
            {blurMode === BlurMode.backdropFilter &&
              Platform.select({
                default: (
                  <BlurView
                    tint="light"
                    intensity={100}
                    style={[StyleSheet.absoluteFill]}
                  />
                ),
                web: (
                  <div
                    className="absolute left-0 top-0 h-full w-full"
                    style={{ backdropFilter: `blur(${blur}px)` }}
                  />
                ),
              })}
          </View>
        )}

        {gradient}

        <Animated.View
          className="absolute bottom-0 right-0 h-full w-full justify-end"
          style={imageWrapperStyle}
        >
          {image && (
            <Animated.View
              className="h-full w-full"
              style={stylePressedImageWrapper}
            >
              <Image
                source={image}
                contentFit="contain"
                cachePolicy="memory-disk"
                transition={300}
                contentPosition="bottom right"
                style={[
                  {
                    flex: 1,
                  },
                  imageStyle,
                ]}
                {...imageProps}
              />
            </Animated.View>
          )}
        </Animated.View>
        {magicGlow && <MagicGlow borderRadius={borderRadius} />}

        {children}

        {/* <Border /> */}
      </Animated.View>
    </>
  )
}
